import './index.less';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { PagerIndex, PagerButton, PagerDot } from './items';

/**
 * 页码展示组件
 *
 * @class Pager
 * @extends {Component}
 */
class Pager extends Component {
    constructor(props) {
        super(props);

        // 若 page 大于可用 length，则使用 length 作为当前页
        const length = this.calcLength(props.total, props.pageSize);
        const page = props.page > length ? length : props.page;

        this.state = {
            page
        };
        this.pagerItems = [];
        this.oldPage = page;

        this.calcLength = this.calcLength.bind(this);
        this.onJumpPage = this.onJumpPage.bind(this);
        this.jumpTo = this.jumpTo.bind(this);
        this.updatePagerItems = this.updatePagerItems.bind(this);
        this.renderPagerItems = this.renderPagerItems.bind(this);
    }

    componentWillReceiveProps(props) {
        // 检查当前 props 的 page 是否和上一个 props 的 page 相同，
        // 同时这里还需要将 props 的 page 和 state 的 page 进行比较以确认是否是重复触发
        //
        // 如果 props.onChange 被指定，onJumpPage 可能会触发两次 setState() 而导致出现两次 render，
        // 并同时触发两次 componentWillReceiveProps()
        // 第一次是组件自身 setState，第二次是 props 被父组件修改而触发这里的 this.jumpTo()
        //
        // 若不做第二项检查，假设在一次回调后父组件主动修改了 props，这时再次进行相同的页面跳转，
        // 会导致新的 props 的重复触发不被处理，尽管它和 state 的值并不相同


        // 修复无法重新跳转第一页的问题
        if (props.forceJump) {
            this.jumpTo(props.page);
            this.oldPage = this.state.page;
            return;
        }


        if (this.oldPage !== props.page && props.page !== this.state.page) {
            this.jumpTo(props.page);
            this.oldPage = this.state.page;
        }
    }

    /**
     * 依据 total 和 pageSize 计算总页数
     *
     * @param {number} total - 总条目数
     * @param {number} pageSize - 每页条目数
     * @returns {number} 总页数
     * @memberof Pager
     */
    calcLength(total, pageSize) {
        return Math.ceil(total / pageSize);
    }

    /**
     * 跳转页面的回调函数，若指定 props.onChange() 则将跳转页码传入该回调函数
     *
     * @param {number} page - 需要跳转的页码
     * @memberof Pager
     */
    onJumpPage(page) {
        // 更新组件内 page 状态
        this.jumpTo(page, () => {
            if (this.props.onChange instanceof Function) {
                this.props.onChange(page);
            }
        });
    }

    jumpTo(page, callback) {
        this.setState({
            page
        }, callback);
    }

    /**
     * 刷新子页码 items 方法，当 page 或 total 等 props 改变时调用，
     * 会将新的 items 存放在 this.pagerItems，调用 this.renderPagerItems() 获取实际组件列表
     *
     * @memberof Pager
     */
    updatePagerItems() {
        const props = this.props;
        const items = [];
        const page = this.state.page;
        const length = this.calcLength(props.total, props.pageSize);
        const padding = props.maxPadding;
        const hideDisabled = props.hideDisabled;

        // 上一页按钮，若指定了 hideDisabled 则不可翻页时将不会展示
        if (!(hideDisabled && page === 1)) {
            items.push(
                {
                    type: 'button', key: 'prev', action: 'prev', isDisabled: page === 1, page: page - 1
                }
            );
        }

        const maxIndexCount = padding * 2 + 3;
        const middleIndexOfSelected = padding + 2;

        const pushIndexRange = (start, end) => {
            for (let i = start; i <= end; i++) {
                items.push(
                    {
                        type: 'index', key: i, isSelected: page === i, page: i
                    }
                );
            }
        };

        if (props.needIndexes) {
            // 页码总数小于等于最大 index 上限，直接输出
            if (length <= maxIndexCount) {
                pushIndexRange(1, length);
            } else {
                // 当前页码小于等于最大 index 上限的一半，输出首页到 (最大 index 上限 - 1) 再输出末页
                if (page <= middleIndexOfSelected) {
                    pushIndexRange(1, maxIndexCount - 1);
                    items.push(
                        { type: 'dot', key: 'next-dot' },
                        {
                            type: 'index', key: length, isSelected: false, page: length
                        }
                    );
                }
                // 当前页码大等于 (总页数 - 最大 index 上限的一半 + 1)，输出首页，再输出 (length - 最大 index 上限 + 2) 到末页
                else if (page >= length - middleIndexOfSelected + 1) {
                    items.push(
                        {
                            type: 'index', key: 1, isSelected: false, page: 1
                        },
                        { type: 'dot', key: 'prev-dot' }
                    );
                    pushIndexRange(length - maxIndexCount + 2, length);
                }
                // 位于中间值，输出首页，(page - padding) 至 (page + padding) 和末页
                else {
                    items.push(
                        {
                            type: 'index', key: 1, isSelected: false, page: 1
                        },
                        { type: 'dot', key: 'prev-dot' }
                    );
                    pushIndexRange(page - padding, page + padding);
                    items.push(
                        { type: 'dot', key: 'next-dot' },
                        {
                            type: 'index', key: length, isSelected: false, page: length
                        }
                    );
                }
            }
        }

        // 下一页按钮，若指定了 hideDisabled 则不可翻页时将不会展示
        if (!(hideDisabled && page === length)) {
            items.push(
                {
                    type: 'button', key: 'next', action: 'next', isDisabled: page === length, page: page + 1
                }
            );
        }

        this.pagerItems = items;
    }

    /**
     * 生成页码子组件列表
     *
     * @returns {Component[]} 子组件列表
     * @memberof Pager
     */
    renderPagerItems() {
        return this.pagerItems.map((elem) => {
            // 获取 elem 的属性作为 props，去除不需要作为 props 的 `type` 属性
            const props = { ...elem };
            delete props.type;
            let item = null;

            // 依据不同的 type 返回不同的组件
            switch (elem.type) {
                case 'button':
                    item = (
                        <PagerButton {...props} onJumpPage={this.onJumpPage} />
                    );
                    break;

                case 'index':
                    item = (
                        <PagerIndex {...props} onJumpPage={this.onJumpPage} />
                    );
                    break;

                case 'dot':
                    item = (
                        <PagerDot {...props} />
                    );
                    break;
            }

            return item;
        });
    }

    render() {
        // 渲染时先更新 items 列表，再渲染列表
        this.updatePagerItems();
        const items = this.renderPagerItems();

        return (
            <div className="u-pager">
                {items}
            </div>
        );
    }
}

Pager.propTypes = {
    /** 需要计算页码数量的总条目数 */
    total: PropTypes.number.isRequired,
    /** 每页展示的条目数 */
    pageSize: PropTypes.number,
    /** 当前页码 */
    page: PropTypes.number,
    /** 是否需要展示中间的页码 */
    needIndexes: PropTypes.bool,
    /** 是否需要隐藏不可点击的按钮 */
    hideDisabled: PropTypes.bool,
    /** 当前页码前后可跳转的页码的最大数量 */
    maxPadding: PropTypes.number,
    /** 改变页码时的回调函数 */
    onChange: PropTypes.func,
    /** 解决无法跳回第一页的问题 */
    forceJump: PropTypes.bool,
};

Pager.defaultProps = {
    total: 0,
    pageSize: 15,
    page: 1,
    needIndexes: true,
    hideDisabled: false,
    maxPadding: 3,
    forceJump: false
};

export default Pager;



// WEBPACK FOOTER //
// ./client/component/pager/index.js